import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { OrgServiceItem, QuoteType } from '../types/admin';

export interface OrgServiceItemInput {
  name: string;
  code?: string;
  party: 'Customer' | 'Mover';
  note?: string;
  quoteType?: QuoteType;
}

export function useOrgServices(orgId: string) {
  const [items, setItems] = useState<OrgServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: OrgServiceItem[] }>(
        `/org-admin/${orgId}/services`,
      );
      setItems(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createItem = async (input: OrgServiceItemInput): Promise<OrgServiceItem> => {
    const res = await api.post<{ success: boolean; data: OrgServiceItem }>(
      `/org-admin/${orgId}/services`,
      input,
    );
    await fetchItems(); // Refetch so ordering is correct
    return res.data;
  };

  const updateItem = async (
    id: string,
    input: Partial<OrgServiceItemInput>,
  ): Promise<OrgServiceItem> => {
    const res = await api.patch<{ success: boolean; data: OrgServiceItem }>(
      `/org-admin/${orgId}/services/${id}`,
      input,
    );
    setItems(prev => prev.map(item => (item.id === id ? res.data : item)));
    return res.data;
  };

  const deleteItem = async (id: string): Promise<void> => {
    await api.del(`/org-admin/${orgId}/services/${id}`);
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const reorderItems = async (orderedIds: string[]): Promise<void> => {
    await api.post(`/org-admin/${orgId}/services/reorder`, {
      order: orderedIds.map(id => ({ id })),
    });
    await fetchItems();
  };

  const syncFromMaster = async (): Promise<{ added: number; updated: number }> => {
    const res = await api.post<{ success: boolean; data: { added: number; updated: number } }>(
      `/org-admin/${orgId}/services/sync-from-master`,
    );
    await fetchItems();
    return res.data;
  };

  return {
    items,
    loading,
    error,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
    syncFromMaster,
    refetch: fetchItems,
  };
}
